import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Field, useFormikContext } from 'formik';
import { Button, useModal, useToastr } from '@farmshare/ui-components';
import { UploadImageModal } from '../components/upload-image-modal';

export function TrimDescriptionsSection() {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const { ask } = useModal();
  const { push } = useToastr();

  const { values, setFieldValue } = useFormikContext<{
    trim?: any[];
    trimImageUrl?: string;
    trimImage?: File;
  }>();
  const trim = values.trim || [];

  return (
    <Accordion
      sx={{
        mb: 2,
        '&:before': {
          display: 'none',
        },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        '&.Mui-expanded': {
          my: '16px !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
        sx={{
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.02)',
          },
          borderRadius: '8px',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography variant="h6">Trim Descriptions</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add descriptions for trim options that will be shown to customers in
          the wizard.
        </Typography>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'stretch',
            }}
          >
            {/* Image preview or Add Image button */}
            <Box
              sx={{
                position: 'relative',
                width: 120,
                minWidth: 120,
                border: 1,
                borderColor: 'divider',
                borderStyle:
                  values.trimImage || values.trimImageUrl ? 'solid' : 'dashed',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
                bgcolor: 'background.paper',
              }}
            >
              {values.trimImage || values.trimImageUrl ? (
                <>
                  <img
                    src={
                      values.trimImage
                        ? URL.createObjectURL(values.trimImage)
                        : values.trimImageUrl
                    }
                    alt="Trim category"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      display: 'flex',
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadModalOpen(true);
                      }}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                        width: 28,
                        height: 28,
                        p: 0.5,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        ask({
                          type: 'ask',
                          title: 'Remove Image',
                          body: 'Are you sure you want to remove the trim category image?',
                          onConfirm: async () => {
                            try {
                              await setFieldValue('trimImageToRemove', true);
                              await setFieldValue('trimImage', undefined);
                              await setFieldValue('trimImageUrl', undefined);
                            } catch (error) {
                              push({
                                title: 'Error',
                                body:
                                  error instanceof Error
                                    ? error.message
                                    : 'Failed to remove image',
                                severity: 'error',
                                delay: 5000,
                              });
                            }
                          },
                        });
                      }}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'error.contrastText',
                        },
                        width: 28,
                        height: 28,
                        p: 0.5,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setUploadModalOpen(true);
                  }}
                  variant="outline-primary"
                  color="primary"
                  content="Add Image"
                  sx={{
                    m: 0,
                    borderStyle: 'dashed',
                    width: '100%',
                    height: '100%',
                    minHeight: '100%',
                  }}
                />
              )}
            </Box>
            <Field name="trimDescription">
              {({ field }: any) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  multiline
                  rows={2}
                  label="Category Description"
                  placeholder="Describe the Trim step..."
                  helperText="Describe the Trim step for customers"
                />
              )}
            </Field>
          </Box>

          {trim.map((trimItem: any, trimIndex: number) => (
            <Field
              key={trimItem._id || trimIndex}
              name={`trim.${trimIndex}.description`}
            >
              {({ field }: any) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  fullWidth
                  multiline
                  rows={2}
                  label={trimItem.name}
                  placeholder={`Describe ${trimItem.name}...`}
                  helperText="Help customers understand this trim option"
                />
              )}
            </Field>
          ))}
        </Stack>
      </AccordionDetails>
      {uploadModalOpen && (
        <UploadImageModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          specificationIndex={0}
          currentImageUrl={
            values.trimImageUrl ||
            (values.trimImage
              ? URL.createObjectURL(values.trimImage)
              : undefined)
          }
          onImageSelected={async (file: File) => {
            try {
              await setFieldValue('trimImage', file);
              await setFieldValue('trimImageToRemove', false);
            } catch (error) {
              push({
                title: 'Error',
                body:
                  error instanceof Error
                    ? error.message
                    : 'Failed to set image',
                severity: 'error',
                delay: 5000,
              });
            }
          }}
        />
      )}
    </Accordion>
  );
}
